import { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { CheckCircle, AlertCircle, Mail } from 'lucide-react';
import { LogoLoader } from '@/components/ui/logo-loader';

export default function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const { t, i18n } = useTranslation(['common', 'pages.verifyEmail']);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');
  const [isResending, setIsResending] = useState(false);
  const { toast } = useToast();

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token || token.length !== 40) {
        setIsVerified(false);
        setErrorMessage(t('pages.verifyEmail:errors.invalidToken'));
        setIsVerifying(false);
        return;
      }

      try {
        const response = await apiClient.verifyEmail(token);
        if (response.success) {
          setIsVerified(true);
          toast({
            title: t('pages.verifyEmail:success.title'),
            description: t('pages.verifyEmail:success.message'),
          });
          // Redirect to login after 3 seconds
          setTimeout(() => {
            setLocation('/login');
          }, 3000);
        } else {
          setIsVerified(false);
          const errorMsg = response.message || t('pages.verifyEmail:errors.verificationFailed');
          
          // Check for specific error types
          if (errorMsg.toLowerCase().includes('expired')) {
            setErrorMessage(t('pages.verifyEmail:errors.expiredToken'));
          } else if (errorMsg.toLowerCase().includes('already verified') || errorMsg.toLowerCase().includes('already_verified')) {
            setErrorMessage(t('pages.verifyEmail:errors.alreadyVerified'));
            setIsVerified(true); // Show success state if already verified
          } else {
            setErrorMessage(errorMsg);
          }
        }
      } catch (error: any) {
        console.error('Email verification error:', error);
        setIsVerified(false);
        const errorMsg = error?.message || t('pages.verifyEmail:errors.verificationFailed');
        
        if (errorMsg.toLowerCase().includes('expired')) {
          setErrorMessage(t('pages.verifyEmail:errors.expiredToken'));
        } else if (errorMsg.toLowerCase().includes('already verified') || errorMsg.toLowerCase().includes('already_verified')) {
          setErrorMessage(t('pages.verifyEmail:errors.alreadyVerified'));
          setIsVerified(true);
        } else {
          setErrorMessage(errorMsg);
        }
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token, t, setLocation, toast]);

  const handleResend = async () => {
    if (!email || !email.includes('@')) {
      toast({
        title: t('pages.verifyEmail:errors.invalidEmail'),
        variant: 'destructive',
      });
      return;
    }

    setIsResending(true);
    try {
      const response = await apiClient.resendVerificationEmail(email, i18n.language);
      if (response.success) {
        toast({
          title: t('pages.verifyEmail:resend.success'),
          description: t('pages.verifyEmail:resend.message'),
        });
      } else {
        toast({
          title: t('pages.verifyEmail:resend.error'),
          description: response.message || t('pages.verifyEmail:errors.verificationFailed'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      toast({
        title: t('pages.verifyEmail:resend.error'),
        description: error?.message || t('pages.verifyEmail:errors.verificationFailed'),
        variant: 'destructive',
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-8">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            {isVerifying ? (
              <LogoLoader size="sm" />
            ) : isVerified ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <Mail className="h-6 w-6 text-blue-600" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {isVerifying
              ? t('pages.verifyEmail:verifying.title')
              : isVerified
              ? t('pages.verifyEmail:success.title')
              : t('pages.verifyEmail:error.title')}
          </CardTitle>
          <CardDescription>
            {isVerifying
              ? t('pages.verifyEmail:verifying.message')
              : isVerified
              ? t('pages.verifyEmail:success.message')
              : t('pages.verifyEmail:error.message')}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {isVerifying && (
            <div className="text-center py-4">
              <p className="text-sm text-gray-600">{t('pages.verifyEmail:verifying.description')}</p>
            </div>
          )}

          {isVerified && (
            <div className="text-center py-4">
              <p className="text-sm text-green-600 mb-4">{t('pages.verifyEmail:success.description')}</p>
              <p className="text-xs text-gray-500">{t('pages.verifyEmail:success.redirect')}</p>
            </div>
          )}

          {!isVerifying && !isVerified && (
            <div className="space-y-4">
              {errorMessage && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{errorMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  {t('pages.verifyEmail:resend.emailLabel')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('pages.verifyEmail:resend.emailPlaceholder')}
                  className="textbox-base w-full"
                />
                <Button
                  onClick={handleResend}
                  disabled={isResending || !email}
                  className="w-full"
                >
                  {isResending ? (
                    <>
                      <LogoLoader size="sm" />
                      {t('pages.verifyEmail:resend.sending')}
                    </>
                  ) : (
                    t('pages.verifyEmail:resend.button')
                  )}
                </Button>
              </div>
            </div>
          )}

          <div className="text-center pt-4">
            <Link href="/login">
              <Button variant="outline" className="w-full">
                {t('pages.verifyEmail:backToLogin')}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

